import * as THREE from 'three';
import { mulberry32 } from '@/core/rng';

/**
 * Rain, as the eye meets it: a shell of falling streaks riding with the
 * camera. Nothing here is world content — a raindrop is not a thing, it is
 * the state of the air — so the streaks live in camera space and only their
 * *fall* is animated, in the vertex shader, off the one clock uniform.
 *
 * The shell is a cylinder of ~1,100 streaks around the eye; each streak's
 * base position is seeded once, and the shader wraps its fall through the
 * shell's height, sheared a little downwind so rain arrives at the slant it
 * really does. Intensity fades the material and, below ~0.35, thins the
 * streaks to a drizzle by discarding the tail of the seeded set — the same
 * omission-only thinning everything else in this world does.
 */
export interface Rain {
  group: THREE.Group;
  /** Per frame: clock seconds, weather rain 0..1, and the eye's position. */
  update(tSec: number, intensity: number, x: number, y: number, z: number): void;
  dispose(): void;
}

const COUNT = 1100;
/** Shell radius and height, metres — past this the streaks are sub-pixel. */
const R = 30, H = 26;
/** Streak length in metres, and the downwind shear per metre of fall. */
const LEN = 1.35;
const SHEAR_X = 0.16, SHEAR_Z = 0.05;   // the island's one wind, leaned into

export function createRain(): Rain {
  const group = new THREE.Group();
  group.name = 'rain';

  // two vertices per streak; aSeed.xyz is the base spot, .w marks which end
  const pos = new Float32Array(COUNT * 2 * 3);
  const seedA = new Float32Array(COUNT * 2 * 4);
  const rnd = mulberry32(0x9a1d);
  for (let i = 0; i < COUNT; i++) {
    const a = rnd() * Math.PI * 2;
    const r = Math.sqrt(rnd()) * R;
    const bx = Math.cos(a) * r, bz = Math.sin(a) * r;
    const by = rnd() * H;
    for (let e = 0; e < 2; e++) {
      const o = (i * 2 + e) * 4;
      seedA[o] = bx; seedA[o + 1] = by; seedA[o + 2] = bz; seedA[o + 3] = e;
      // rank in [0,1) by index: intensity thins the tail off
      pos[(i * 2 + e) * 3] = i / COUNT;
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('aSeed', new THREE.BufferAttribute(seedA, 4));
  // the shell rides the camera; its bounds are wherever it happens to be
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), R + H);

  const uniforms = {
    uTime: { value: 0 },
    uRain: { value: 0 },
  };
  const mat = new THREE.ShaderMaterial({
    uniforms,
    transparent: true,
    depthWrite: false,
    vertexShader: /* glsl */ `
      attribute vec4 aSeed;
      uniform float uTime;
      uniform float uRain;
      varying float vFade;
      void main() {
        // rank thinning: light rain draws the head of the set, never dimmer
        // copies of all of it — a drizzle is fewer drops, not ghost drops
        float rank = position.x;
        float keep = step(rank, uRain * 1.6 + 0.08);
        // the fall, wrapped through the shell: ~14 m/s, seeded phase
        float drop = mod(aSeed.y - uTime * 14.0, ${H.toFixed(1)});
        float end = aSeed.w * ${LEN.toFixed(2)};
        vec3 p = vec3(
          aSeed.x + (drop + end) * ${SHEAR_X.toFixed(2)},
          drop + end,
          aSeed.z + (drop + end) * ${SHEAR_Z.toFixed(2)});
        vFade = keep * uRain;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      varying float vFade;
      void main() {
        if (vFade < 0.01) discard;
        gl_FragColor = vec4(0.72, 0.78, 0.85, 0.34 * vFade);
      }`,
  });

  const lines = new THREE.LineSegments(geo, mat);
  lines.frustumCulled = false;
  group.add(lines);
  group.visible = false;

  return {
    group,
    update(tSec, intensity, x, y, z) {
      group.visible = intensity > 0.03;
      if (!group.visible) return;
      uniforms.uTime.value = tSec;
      uniforms.uRain.value = intensity;
      // the shell hangs from just above the eye, wherever the eye is
      group.position.set(x, y - H * 0.4, z);
    },
    dispose() {
      geo.dispose();
      mat.dispose();
    },
  };
}
